import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

/**
 * Custom crosshair cursor (desktop only). Follows the mouse with spring physics,
 * spreads over any interactive element, and shows TARGET LOCKED + corner bracket
 * framing when hovering product cards. Hidden until first mouse movement; never
 * renders on touch devices.
 */
export default function Crosshair() {
  const [enabled, setEnabled] = useState(false);
  const [visible, setVisible] = useState(false);
  const [hot, setHot] = useState(false);      // over a link/button
  const [locked, setLocked] = useState(false); // over a product card
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const sx = useSpring(x, { stiffness: 900, damping: 55, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 900, damping: 55, mass: 0.4 });

  useEffect(() => {
    if (!window.matchMedia("(pointer: fine)").matches) return undefined;
    setEnabled(true);
    document.documentElement.classList.add("crosshair-on");

    const move = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      setVisible(true);
      const card = e.target.closest?.(".card-inj");
      setLocked(Boolean(card));
      setHot(Boolean(card) || Boolean(e.target.closest?.("a, button, input, textarea, select, label")));
    };
    const leave = () => setVisible(false);
    window.addEventListener("mousemove", move, { passive: true });
    document.documentElement.addEventListener("mouseleave", leave);
    return () => {
      window.removeEventListener("mousemove", move);
      document.documentElement.removeEventListener("mouseleave", leave);
      document.documentElement.classList.remove("crosshair-on");
    };
  }, [x, y]);

  if (!enabled || !visible) return null;

  const spread = locked ? 14 : hot ? 11 : 7;
  const tick = locked ? 12 : hot ? 9 : 6;

  return (
    <motion.div
      data-testid="crosshair-cursor"
      className="fixed top-0 left-0 z-[300] pointer-events-none mix-blend-difference"
      style={{ x: sx, y: sy }}
    >
      <div className="relative" style={{ transform: "translate(-50%, -50%)" }}>
        {/* center dot */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-1 bg-white" />
        {/* 4 ticks */}
        {[
          { style: { left: "50%", top: -spread - tick, transform: "translateX(-50%)", width: 2, height: tick } },
          { style: { left: "50%", top: spread, transform: "translateX(-50%)", width: 2, height: tick } },
          { style: { top: "50%", left: -spread - tick, transform: "translateY(-50%)", height: 2, width: tick } },
          { style: { top: "50%", left: spread, transform: "translateY(-50%)", height: 2, width: tick } },
        ].map((t, i) => (
          <div key={i} className="absolute bg-white transition-all duration-150" style={t.style} />
        ))}
        {/* lock ring */}
        <div
          className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 border border-white rounded-full transition-all duration-200 ${locked ? "w-12 h-12 opacity-100" : "w-6 h-6 opacity-0"}`}
        />
        {/* TARGET LOCKED label */}
        <div
          data-testid="crosshair-lock-label"
          className={`absolute left-6 top-6 font-mono text-[9px] tracking-[0.25em] text-white whitespace-nowrap transition-opacity duration-150 ${locked ? "opacity-100" : "opacity-0"}`}
        >
          TARGET LOCKED
        </div>
      </div>
    </motion.div>
  );
}
